import React from 'react';
import { Box } from '@mui/material';

import { GlobalDataTable } from '@/components/global-data-table';
import { ColumnConfig } from '@/components/types/types';
import { useTableConfig } from '@/components/hooks/use-table-config';

interface BaseDataTableProps<T> {
  data?: T[];
  loading?: boolean;
  currentPage?: number;
  itemsPerPage?: number;
  totalItems?: number;
  onPageChange?: (page: number) => void;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  onRowClick?: (item: T) => void;
  sortField?: string | null;
  sortOrder?: 'asc' | 'desc';
  onSortChange?: (field: string, order: 'asc' | 'desc') => void;
  columns: ColumnConfig<T>[];
  getRowStyle?: (item: T) => React.CSSProperties;
  hidePagination?: boolean;
  enableServerSort?: boolean;
  wrapperStyles?: React.CSSProperties;
}

const BaseDataTable = <T extends Record<string, unknown>>({
  data = [],
  loading = false,
  currentPage = 1,
  itemsPerPage = 100,
  totalItems = 0,
  onPageChange,
  onItemsPerPageChange,
  onRowClick,
  sortField = null,
  sortOrder = 'desc',
  onSortChange,
  columns,
  getRowStyle,
  hidePagination = false,
  enableServerSort = true,
  wrapperStyles,
}: BaseDataTableProps<T>) => {
  // Table configuration
  const { tableConfig } = useTableConfig({
    columns,
    currentPage,
    itemsPerPage,
    sortField,
    sortOrder,
    onSortChange,
    enableServerSort,
    hidePagination,
    getRowStyle,
  });

  // Handle row click
  const handleRowClick = (item: T) => {
    onRowClick?.(item);
  };

  const content = (
    <GlobalDataTable<T>
      data={data}
      loading={loading}
      config={tableConfig}
      onRowClick={handleRowClick}
      currentPage={currentPage}
      itemsPerPage={itemsPerPage}
      totalItems={totalItems}
      onPageChange={onPageChange}
      onItemsPerPageChange={onItemsPerPageChange}
      sortField={sortField}
      sortOrder={sortOrder}
    />
  );

  if (wrapperStyles) {
    return <Box sx={wrapperStyles}>{content}</Box>;
  }

  return content;
};

export default BaseDataTable;
